import React, { useState, useEffect } from 'react';
import { Lock, Mail } from 'lucide-react';
import styles from './OAuthLogin.module.css';
import { useAuthStore } from '../../store/authStore';
import SsoSection from '../../components/sso/SsoSection';
import SecureInput from '../../ui/SecureInput/SecureInput';
import logoIcon from '../../assets/logo_icon.png';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../../lib/apiClient';
import { useAccountBlock } from '../../hooks/useAccountBlock';

export default function OAuthLoginPage() {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { timeRemaining, isBlocked, handleError: handleBlockError, clearBlock, checkBeforeSubmit } = useAccountBlock();
  const [oauthParams, setOauthParams] = useState<Record<string, string>>({});
  const [currentUser, setCurrentUser] = useState<{ email: string; first_name?: string } | null>(null);
  const [hasSession, setHasSession] = useState(false);
  const { loginWithOAuth, isAuthenticated, logout } = useAuthStore(state => state);
  const navigate = useNavigate();

  async function apiLogin(email: string, password: string) {
    const response = await loginWithOAuth(email, password, oauthParams.sessionId);
    return response;
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('sessionId') || '';
    const sessionFlag = urlParams.get('hasSession') === 'true';

    if (!sessionId && isAuthenticated) {
      navigate('/dashboard', { replace: true });
      return;
    }

    setHasSession(sessionFlag);
    setOauthParams({
      client_id: urlParams.get('client_id') || '',
      redirect_uri: urlParams.get('redirect_uri') || '',
      state: urlParams.get('state') || '',
      scope: urlParams.get('scope') || '',
      sessionId,
      response_type: urlParams.get('response_type') || 'code',
    });

    if (sessionFlag) {
      authApi.getCurrentUser()
        .then((user: any) => setCurrentUser(user))
        .catch(() => setHasSession(false));
    }
  }, [isAuthenticated]);

  const handleContinueSession = async () => {
    setLoading(true);
    setError('');
    try {
      const response: any = await authApi.continueSession(oauthParams.sessionId);
      if (response.redirectUrl) {
        window.location.href = response.redirectUrl;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to continue session');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchAccount = async () => {
    setLoading(true);
    try {
      await logout();
    } catch {
      // ignore
    } finally {
      setHasSession(false);
      setCurrentUser(null);
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkBeforeSubmit()) return;
    setLoading(true);
    setError('');
    try {
      const response = await apiLogin(formData.email, formData.password);
      if (response.redirectUrl) {
        clearBlock();
        window.location.href = response.redirectUrl;
        return;
      }
    } catch (err: any) {
      handleBlockError(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.bg}>
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.logo}>
              <img src={logoIcon} alt="Logo" className={styles.logoImage} />
            </div>
            <h1 className={styles.title}>Sign In</h1>
            <p className={styles.subtitle}>Access your Oitools Studio</p>
          </div>

          {oauthParams.client_id && (
            <div className={styles.info}>
              <div className={styles.infoTitle}>
                <span>Authorization Request</span>
              </div>
              <p className={styles.infoDesc}>
                AI client requesting access to your account
              </p>
            </div>
          )}

          {error && (
            <div className={styles.error}>
              <p>{error}</p>
            </div>
          )}

          {hasSession && currentUser ? (
            <div className={styles.sessionSection}>
              <p className={styles.sessionText}>
                Currently signed in as <strong>{currentUser.email}</strong>
              </p>
              <div className={styles.sessionActions}>
                <button
                  type="button"
                  onClick={handleContinueSession}
                  disabled={loading}
                  className={styles.button}
                >
                  {loading ? <span className={styles.spinner}></span> : `Continue as ${currentUser.first_name || currentUser.email}`}
                </button>
                <button
                  type="button"
                  onClick={handleSwitchAccount}
                  disabled={loading}
                  className={`${styles.button} ${styles.secondary}`}
                >
                  Sign in as different user
                </button>
              </div>
            </div>
          ) : (
            <>
              <form onSubmit={handleLogin} className={styles.form}>
                <div className={styles.field}>
                  <SecureInput
                    fieldType="email"
                    label="Email Address"
                    icon={<Mail size={16} />}
                    value={formData.email}
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, email: value }));
                      if (error) setError('');
                    }}
                    placeholder="Enter your email"
                    disabled={loading}
                    required
                    autoComplete="email"
                    className={styles.input}
                  />
                </div>

                <div className={styles.field}>
                  <SecureInput
                    fieldType="password"
                    label="Password"
                    icon={<Lock size={16} />}
                    value={formData.password}
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, password: value }));
                      if (error) setError('');
                    }}
                    placeholder="Enter your password"
                    disabled={loading}
                    required
                    autoComplete="current-password"
                    skipPasswordValidation={true}
                    className={styles.input}
                  />
                </div>

                <div className={styles.actions}>
                  {oauthParams.client_id ? (
                    <div className={styles.btnRow}>
                      <button
                        type="submit"
                        disabled={loading || isBlocked}
                        className={styles.button}
                      >
                        {isBlocked ? `Try again in ${timeRemaining}` : loading ? <span className={styles.spinner}></span> : 'Sign In'}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="submit"
                      disabled={loading || isBlocked}
                      className={styles.button + ' ' + styles.authorize + ' ' + styles.full}
                    >
                      {isBlocked ? `Try again in ${timeRemaining}` : loading ? <span className={styles.spinner}></span> : 'Sign In'}
                    </button>
                  )}
                </div>
              </form>
              <SsoSection loading={loading} oauthParams={oauthParams} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
